import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { getDescendants } from '@minoru/react-dnd-treeview'
import { nanoid } from 'nanoid'
import { showToast, toastStore } from 'shared/ui'
import { type PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { InboxTree } from 'entities/Inbox/model/Dnd/InboxTree'
import {
  type IInboxCombine,
  type IInboxTreeCombine,
  InboxesApi,
  NAME_INBOX,
  TreeRootInboxes,
} from 'entities/Inbox'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { type Phone } from 'entities/Phone/model/Phone'
import { type IResponseInboxCombine } from 'entities/Inbox/api/types'
import { groupInboxTemplate } from 'entities/Inbox/templates/groupInboxTemplate'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SmartView } from 'entities/SmartViews/model/SmartView'
import { type CallModalStore } from 'widgets/CallModal'
import { type ContactCreateModalStore } from 'widgets/ContactCreateModal'
import {
  type IAddItemTree,
  type IConversationMenuStoreConfig,
  type IHandleAddTeamsGroupsCreateLocal,
  type IHandleUpdateTeamsFavorite,
} from 'pages/conversations/ui/ConversationMenu/store/types'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'
import { ConversationRoutes } from 'pages/conversations/route/type'
import { QuickFiltersMenuStore } from 'pages/conversations/ui/ConversationMenu/store/quickFiltersMenuStore'
import { SmartViewsMenuStore } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuSectionSmartViewsTree'

export class ConversationMenuStore {
  private _inboxesTreeMap: Map<string, InboxTree> = new Map()
  private _search = ''
  private _searchSimple = ''
  private _searchFavorite = ''
  private _error = false
  private _loading = false
  private _loadingCreateGroupTeam = false
  private _loadingUpdateTeam = false
  private _loadingUser = true
  private _disabledActive = false
  private _createFromGroupInbox: GroupInbox | null = null
  private _disposerInboxSize: IReactionDisposer | null = null
  private _config: IConversationMenuStoreConfig | null = null

  readonly quickFiltersMenuStore: QuickFiltersMenuStore
  readonly smartViewsMenuStore: SmartViewsMenuStore

  constructor(
    private _pageLayoutStore: PageLayoutStore,
    private _callModalStore: CallModalStore,
    private _contactCreateModalStore: ContactCreateModalStore,
    private _entities: IEntitiesStore
  ) {
    this.quickFiltersMenuStore = new QuickFiltersMenuStore(this, this._entities)
    this.smartViewsMenuStore = new SmartViewsMenuStore(this, this._entities)

    makeAutoObservable(this)
  }

  reactionInboxSize = () => {
    this._disposerInboxSize?.()
    this._disposerInboxSize = reaction(
      () => this._entities.inboxesStore.inboxesMap.size,
      () => {
        this._disposeTree()
        this.initInboxesTree()
      }
    )
  }

  setConfig = (config?: IConversationMenuStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  setDisabledActive = (value: boolean) => {
    this._disabledActive = value
  }

  private _disposeTree = () => {
    this._inboxesTreeMap.clear()
    this._disabledActive = false
  }

  dispose = () => {
    this.smartViewsMenuStore.dispose()
    this._disposeTree()
    this._disposerInboxSize?.()
  }

  setLoadingUser = (value: boolean) => {
    this._loadingUser = value
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    this.reactionInboxSize()

    await this._entities.featureFlagsStore.fetchFeatureFlags()
    await Promise.all([this.initInboxes(), this.quickFiltersMenuStore.initQuickFilters()])

    runInAction(() => {
      this._loading = false
    })
  }

  initInboxes = async () => {
    try {
      await Promise.all([
        this._entities.inboxesStore.fetchInboxes(),
        this._entities.smartViewsStore.fetchSmartViews(),
      ])

      this._disposeTree()

      this.initInboxesTree()

      runInAction(() => {
        this._error = false
      })
    } catch (e) {
      logger.error(e)
    }
  }

  initInboxesTree = () => {
    const inboxes = Array.from(this._entities.inboxesStore.inboxesMap.values())
    const smartViews = Array.from(this._entities.smartViewsStore.itemsMap.values())

    this._inboxesTreeMap = this.addItemsTree([...inboxes, ...smartViews])
  }

  handleUpdateSearchSimple = (value: string) => {
    this._searchSimple = value
  }

  handleUpdateSearchFavorite = (value: string) => {
    this._searchFavorite = value
  }

  addItemsTree = (inboxes: IInboxTreeCombine[]) => {
    inboxes.forEach((inbox) => {
      this.addItemTree({
        inbox,
      })
    })

    return this._inboxesTreeMap
  }

  addItemTree = ({ inbox, groupInbox }: IAddItemTree) => {
    if (inbox instanceof GroupInbox) {
      const hasAccessibleChildren = inbox.teamsIds.some(
        (teamId) => !!this._entities.inboxesStore.getItem(teamId)
      )

      if (!hasAccessibleChildren) return this._inboxesTreeMap
    }

    this._inboxesTreeMap.set(inbox.idTree, new InboxTree(inbox))

    if (groupInbox instanceof GroupInbox) {
      this._createFromGroupInbox = groupInbox
    }

    if (inbox instanceof GroupInbox) {
      inbox.teamsIds.forEach((teamId) => {
        const inboxChild = this._entities.inboxesStore.getItem(teamId)

        if (!inboxChild) return

        const id = `${inbox.root_tree}_${inbox.id}_${inboxChild.id}`
        inboxChild.root_tree = inbox.root_tree

        this._inboxesTreeMap.set(id, new InboxTree(inboxChild, inbox))
      })
    }

    return this._inboxesTreeMap
  }

  updateItemTree = (id: string, props: { id?: string; parent?: string } = {}) => {
    const inboxTree = this._inboxesTreeMap.get(id)

    if (inboxTree) {
      this.deleteItemTree(inboxTree.id, 'child')

      for (const key in props) {
        if (key === 'id' && props.id) inboxTree.id = props.id
        if (key === 'parent' && props.parent) {
          inboxTree.parent = props.parent
          inboxTree.data.root_tree = props.parent
        }
      }

      this._inboxesTreeMap.set(inboxTree.id, inboxTree)

      if (!inboxTree.data.isGroupInbox) {
        this.updateItemTreeChild(inboxTree.id)
      }

      return inboxTree
    }
  }

  updateItemTreeChild = (id: string) => {
    const inboxTree = this._inboxesTreeMap.get(id)
    const inbox = inboxTree?.data

    if (inboxTree) {
      this._inboxesTreeMap.forEach((item) => {
        const idSplit = item.id.toString().split('_')
        const hasChild = idSplit.length === 3

        if (hasChild) {
          const currentRootTreeParent = idSplit[0]
          const currentIdTreeParent = +idSplit[1]
          const currentIdTreeChild = +idSplit[2]

          if (currentIdTreeChild === inbox?.id) {
            const groupInbox = this._entities.inboxesStore.getItem(currentIdTreeParent)

            if (!groupInbox) return

            const oldIdTree = `${currentRootTreeParent}_${currentIdTreeParent}_${currentIdTreeChild}`
            const newIdTree = `${groupInbox.root_tree}_${currentIdTreeParent}_${currentIdTreeChild}`
            const newRootTree = `${groupInbox.root_tree}_${currentIdTreeParent}`

            this.deleteItemTree(oldIdTree, 'child')

            if (groupInbox?.type === 'inbox_group') {
              if (groupInbox?.teamsIds.map((teamId) => teamId).includes(inbox.id)) {
                item.id = newIdTree
                item.parent = newRootTree
              }
            }

            setTimeout(() => {
              this._inboxesTreeMap.set(newIdTree, item)
            }, 10)
          }
        }
      })
    }
  }

  deleteItemTree = (
    id: string | number,
    type: 'all' | 'child' = 'all',
    variant: 'default' | 'smart' = 'default'
  ) => {
    let inboxTree: InboxTree | undefined
    let inbox: IInboxTreeCombine | undefined
    let idTree

    if (typeof id === 'string') {
      idTree = id
      inboxTree = this._inboxesTreeMap.get(idTree)
      inbox = inboxTree?.data
    } else {
      if (variant === 'smart') {
        inbox = this._entities.smartViewsStore.getItem(id)
      } else {
        inbox = this._entities.inboxesStore.getItem(id)
      }

      idTree = inbox?.idTree
    }

    if (inbox) {
      if (type === 'all') {
        if (inbox instanceof SmartView) {
          this._entities.smartViewsStore.deleteItem(inbox.id)
        } else {
          this._entities.inboxesStore.deleteItem(inbox.id)
        }

        this._inboxesTreeMap.delete(inbox.idTree)
      }
    }

    if (type === 'child') {
      if (idTree) this._inboxesTreeMap.delete(idTree)
    }
  }

  getItemTree = (id?: string) => {
    if (!id) return

    return this._inboxesTreeMap.get(id)
  }

  updateRootIdTree = (inbox: IInboxTreeCombine, dropTargetId: string) => {
    const idTree = inbox.idTree

    this.updateItemTree(idTree, {
      id: `${dropTargetId}_${inbox.id}`,
      parent: dropTargetId,
    })
  }

  handleSearchFilter = (inbox: IInboxTreeCombine, search: string) => {
    let numbers: Phone[] = []
    const inboxes: Inbox[] = []
    const searchByName = inbox.name.toUpperCase().includes(search.toUpperCase())

    if (inbox.type === 'inbox_group') {
      const teamsNumberIds = inbox.teamsIds
        .map((id) => {
          const inboxTemp = this._entities.inboxesStore.getItem(id)

          if (inboxTemp?.type !== 'inbox') return []

          return inboxTemp?.numberIds
        })
        .filter((item) => item)
        .reduce((state, ids) => {
          return [...(state ? state : []), ...(ids ? ids : [])]
        }, [])

      inbox.teamsIds.map((id) => {
        const inboxTemp = this._entities.inboxesStore.getItem(id)

        if (inboxTemp?.type !== 'inbox') return null

        inboxes.push(inboxTemp)
      })

      numbers = Array.isArray(teamsNumberIds)
        ? teamsNumberIds.reduce<Phone[]>((state, id) => {
            const item = this._entities.numbersStore.getItem(id)

            return item ? [...state, item] : state
          }, [])
        : []
    } else if (inbox.type === 'inbox') {
      numbers = inbox.numberIds.reduce<Phone[]>((state, id) => {
        const item = this._entities.numbersStore.getItem(id)

        return item ? [...state, item] : state
      }, [])
    }

    const hasNameIntersection =
      searchByName ||
      !!inboxes.filter((inbox) => inbox.name.toUpperCase().includes(search.toUpperCase())).length
    const hasFormattedNumber = !!numbers
      .map((number) => number?.formatted_number)
      .filter((number) => number?.includes(search)).length
    const hasNationalNumber = !!numbers
      .map((number) => number?.national_number)
      .filter((number) => number?.includes(search)).length
    const hasNumber = !!numbers
      .map((number) => number?.number)
      .filter((number) => number?.includes(search)).length

    return Boolean(
      searchByName || hasFormattedNumber || hasNationalNumber || hasNumber || hasNameIntersection
    )
  }

  get entities() {
    return this._entities
  }

  get itemsFavorites() {
    return getDescendants(this._selectInboxesFavoriteFreeFilter, TreeRootInboxes.FAVORITE)
  } // main

  get itemsInboxes() {
    return getDescendants(this._selectInboxesSimpleFreeFilter, TreeRootInboxes.SIMPLE)
  } // main

  private get _selectInboxesFree() {
    return Array.from(this._inboxesTreeMap.values()).filter(
      (item) => item.data.type !== 'unified-inbox'
    )
  }

  get selectInboxesFreeSort() {
    const sort = {
      unified: 0,
      group: 1,
      shared: 3,
      smart_view: 3,
    }

    return this._selectInboxesFree
      .sort((a, b) => a.data.name.localeCompare(b.data.name))
      .sort((a, b) => {
        const inboxA = a.data
        const inboxB = b.data
        const aValue = sort[inboxA.typeKey]
        const bValue = sort[inboxB.typeKey]

        return aValue - bValue
      })
      .sort((a, b) => {
        const inboxA = a.data.isMuted
        const inboxB = b.data.isMuted

        return Number(inboxA) - Number(inboxB)
      })
  }

  private get _selectInboxesSimpleFreeFilter() {
    if (!this._searchSimple) return this.selectInboxesFreeSort

    return this.selectInboxesFreeSort.filter((inboxTree) => {
      return this.handleSearchFilter(inboxTree.data, this._searchSimple)
    })
  }

  private get _selectInboxesFavoriteFreeFilter() {
    if (!this._searchFavorite) return this.selectInboxesFreeSort

    return this.selectInboxesFreeSort.filter((inboxTree) => {
      return this.handleSearchFilter(inboxTree.data, this._searchFavorite)
    })
  }

  private get _selectInboxesFilter() {
    if (!this._search) return this._entities.inboxesStore.inboxesList

    return this._entities.inboxesStore.inboxesList.filter((inbox) => {
      return this.handleSearchFilter(inbox, this._search)
    })
  }

  private get _selectInboxesFavorites() {
    return this._selectInboxesFilter.filter((item) => item.isFavoriteType)
  }

  get selectInboxesFavoritesCount() {
    return this._selectInboxesFavorites
      .map((item) => item.unread)
      .reduce((state, value) => state + value, 0)
  }

  private get _selectInboxesSimple() {
    return this._selectInboxesFilter.filter((item) => !item.isFavoriteType)
  }

  get selectInboxesSimpleCount() {
    return this._selectInboxesSimple
      .map((item) => item.unread)
      .reduce((state, value) => state + value, 0)
  }

  get selectInboxesGroupViews() {
    return this._selectInboxesFilter.filter((item) => item.isGroupInbox)
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  handleDeleteTeamsGroupsById = async (inbox: IInboxCombine) => {
    this.deleteItemTree(inbox.idTree)
    await this._entities.inboxesStore.handleDeleteTeamsGroupsById(inbox)
  }

  handleCreateTeamsGroupsByIdAddTeams = async (inbox: IInboxCombine, groupId: number) => {
    const groupInbox = this._entities.inboxesStore.getItem(groupId)

    if (groupInbox && inbox.type === 'inbox_group') {
      await this._entities.inboxesStore.handleCreateTeamsGroupsByIdAddTeams(inbox, groupId)
      this.addItemTree({
        inbox: groupInbox,
      })
    }
  }

  handleDeleteTeamsGroupsByIdRemoveTeamById = async (inbox: Inbox, groupInbox?: GroupInbox) => {
    if (!groupInbox) return

    await this._entities.inboxesStore.handleDeleteTeamsGroupsByIdRemoveTeamById(inbox, groupInbox)

    if (!groupInbox.teamsIds.length) {
      this.handleDeleteTeamsGroupsById(groupInbox)
    }

    const nanoId = nanoid()
    let isUndoActionExecuted = false

    toastStore.add({
      id: nanoId,
      noCloseButton: true,
      type: 'info',
      title: `Inbox '${inbox.name}' removed from folder`,
      action: {
        text: 'Undo',
        onAction: async () => {
          if (isUndoActionExecuted) return

          isUndoActionExecuted = true

          runInAction(() => {
            toastStore.remove(nanoId)
          })

          if (groupInbox.teamsIds.length) {
            this.handleCreateTeamsGroupsByIdAddTeams(inbox, groupInbox.id)
          } else {
            groupInbox.teamsIds = [inbox.id]

            const newGroupInbox = await this.handleUpdateTeamsGroupsCreate(
              groupInbox.name,
              groupInbox
            )

            if (inbox.isFavoriteType && newGroupInbox) {
              groupInbox.syncOrigin({ ...groupInbox.origin, id: newGroupInbox.id })
              groupInbox.is_favorite = false
              this.addItemTree({
                inbox,
              })
              this.handleUpdateTeamsFavorite({
                inbox: newGroupInbox,
                status: true,
              })
            }

            groupInbox.handleUpdateTeamsIds([inbox.id])
          }
        },
      },
    })

    if (inbox.is_favorite !== groupInbox.is_favorite) {
      this.deleteItemTree(`${groupInbox.idTree}_${inbox.id}`, 'child')

      const root_tree = inbox.is_favorite ? TreeRootInboxes.FAVORITE : TreeRootInboxes.SIMPLE
      this._inboxesTreeMap.delete(`${inbox.root_tree}_${inbox.id}`)

      inbox.root_tree = root_tree
      this.addItemTree({
        inbox,
      })
    } else {
      this.deleteItemTree(`${groupInbox.idTree}_${inbox.id}`, 'child')
      this.deleteItemFromGroup(groupInbox)
    }
  }

  deleteItemFromGroup = (group: IInboxTreeCombine) => {
    const groupInbox = this._entities.inboxesStore.getItem(group.id)

    if (groupInbox) {
      this.addItemTree({
        inbox: groupInbox,
      })
    }
  }

  handleAddTeamsGroupsCreateLocal = ({ inbox, groupInbox }: IHandleAddTeamsGroupsCreateLocal) => {
    const newInboxCreateId = NAME_INBOX.Create
    const newInboxCreateResponse = {
      ...groupInboxTemplate,
      id: newInboxCreateId,
      teams: [inbox],
      is_favorite: inbox.is_favorite,
    }

    const item = this._entities.inboxesStore.addItem(newInboxCreateResponse)
    if (item) this.deleteItemTree(item.idTree)

    const newInboxGroup = this._entities.inboxesStore.getItem(newInboxCreateId)

    if (newInboxGroup) {
      this.addItemTree({
        inbox: newInboxGroup,
        groupInbox: groupInbox,
      })
    }
  }

  handleUpdateTeamsGroupsCreate = async (name: string, inbox: GroupInbox) => {
    const inboxCreateId = NAME_INBOX.Create

    try {
      runInAction(() => {
        this._loadingCreateGroupTeam = true
      })

      const params = {
        name: name,
        team_ids: inbox.teamsIds,
      }
      const item = this._entities.inboxesStore.getItem(inbox.teamsIds[0])

      if (this._createFromGroupInbox && item instanceof Inbox) {
        await this.handleDeleteTeamsGroupsByIdRemoveTeamById(item, this._createFromGroupInbox)

        this._createFromGroupInbox = null
      }

      const { data } = await InboxesApi.updateTeamsGroupsCreate(params)

      const inboxGroupResponse: IResponseInboxCombine = {
        ...data,
        type: 'inbox_group',
      }

      this._entities.inboxesStore.addItem(inboxGroupResponse)

      const inboxGroup = this._entities.inboxesStore.getItem(inboxGroupResponse.id)

      this.deleteItemTree(inboxCreateId)

      if (inboxGroup) {
        this.addItemTree({
          inbox: inboxGroup,
        })

        if (inbox.isFavoriteType) {
          this.handleUpdateTeamsFavorite({
            inbox: inboxGroup,
            status: !inboxGroup.isFavoriteType,
          })
        }
      }

      return inboxGroup
    } catch (e) {
      logger.error(e)

      this.deleteItemTree(inboxCreateId)

      showToast({
        type: 'error',
        title: 'Folder name should contain min 3 characters',
      })
    } finally {
      runInAction(() => {
        this._loadingCreateGroupTeam = false
      })
    }
  }

  handleUpdateTeamsFavorite = async ({ inbox, groupInbox, status }: IHandleUpdateTeamsFavorite) => {
    const rootId = status
      ? TreeRootInboxes.FAVORITE
      : inbox.isSmartViewInbox
      ? TreeRootInboxes.SMART_VIEW
      : TreeRootInboxes.SIMPLE

    if (groupInbox?.type === 'inbox_group' && inbox.type === 'inbox') {
      await this.handleDeleteTeamsGroupsByIdRemoveTeamById(inbox, groupInbox)
    }

    if (inbox.type === 'smart_view-inbox') {
      await this._entities.smartViewsStore.updateFavorite(inbox, status)
    } else {
      await this._entities.inboxesStore.handleUpdateTeamsFavorite(inbox, status)
    }

    this.updateRootIdTree(inbox, rootId)

    if (inbox.type === 'inbox_group') {
      const requests: Promise<void>[] = []

      inbox.teamsIds.forEach((itemId) => {
        const inbox = this._entities.inboxesStore.getItem(itemId)
        if (!inbox) return
        requests.push(
          this.handleUpdateTeamsFavorite({
            inbox: inbox,
            status: status,
          })
        )
      })

      await Promise.all(requests)
    }
  }

  resetCurrentConversation = () => {
    uiStore.changeRoute({
      path: `/${ConversationsPath.root}`,
    })
    this._entities.conversationStore.setCurrentItemId(null)
  }

  handleUpdateTeamInbox = async (inboxId: number) => {
    runInAction(() => {
      this._loadingUpdateTeam = true
    })

    await this._entities.inboxesStore.handleUpdateTeamInbox(inboxId, 'fast')

    runInAction(() => {
      this._loadingUpdateTeam = false
    })
  }

  get callModalStore() {
    return this._callModalStore
  }

  get contactCreateModalStore() {
    return this._contactCreateModalStore
  }

  get searchSimple() {
    return this._searchSimple
  }

  get loading() {
    return this._loading
  }

  get loadingUser() {
    if (!this._entities.inboxesStore.isUnifiedInbox) return false

    return this._loadingUser
  }

  get loadingCreateGroupTeam() {
    return this._loadingCreateGroupTeam
  }

  get loadingUpdateTeam() {
    return this._loadingUpdateTeam
  }

  get disabledActive() {
    return this._disabledActive
  }

  get search() {
    return this._search
  }

  get searchFavorite() {
    return this._searchFavorite
  }

  get config() {
    return this._config
  }

  get isActiveConversationSearch() {
    return uiStore.pathName.includes(`/${ConversationRoutes.search}`)
  }
}
